import React from 'react';
import PropTypes from 'prop-types';
import { Item, Card, Header, Button } from 'semantic-ui-react';
import EventButton from '../../atoms/EventButton/EventButton';
import Image from '../../atoms/Image';
import './ItemElement.css';

export default class ItemElement extends React.Component {
  constructor(props) {
    super(props);

    const { content } = this.props;
    this.state = {
      currentContent: this.contentExceedsCharLimit() ? this.getShortenedText() : content,
      buttonText: 'See More',
    };

    this.expandOrContractLongText = this.expandOrContractLongText.bind(this);
  }

  getShortenedText() {
    const { content, charLimit } = this.props;

    return content.substr(0, content.lastIndexOf(' ', charLimit)).concat('...');
  }

  contentExceedsCharLimit() {
    const { content, charLimit } = this.props;

    return !(!content || content.length <= charLimit);
  }

  expandOrContractLongText() {
    const { content } = this.props;
    const { buttonText } = this.state;

    if (buttonText === 'See More') {
      this.setState({ currentContent: content, buttonText: 'See Less' });
    }

    if (buttonText === 'See Less') {
      this.setState({ currentContent: this.getShortenedText(), buttonText: 'See More' });
    }
  }

  renderEventButton(fluid = false, floated = null) {
    const { event, buttonSize, buttonBasic, buttonColor } = this.props;
    const { buttonText } = this.state;
    if (event) {
      return (
        <EventButton
          className="itemelement-more-button"
          event={event}
          color={buttonColor}
          fluid={fluid}
          floated={floated}
          primary
          onClick={this.expandOrContractLongText}
          basic={buttonBasic}
          size={buttonSize}
        >
          {buttonText}
        </EventButton>
      );
    }
    return (
      <Button
        className="itemelement-more-button"
        color={buttonColor}
        fluid={fluid}
        primary
        onClick={this.expandOrContractLongText}
        basic={buttonBasic}
        size={buttonSize}
      >
        {buttonText}
      </Button>
    );
  }

  renderCard() {
    const {
      imageCircular,
      imageSize,
      itemCenterContent,
      link,
      linkAs,
      header,
      headerIcon,
      image,
      subheader,
      cardInlinePhoto,
    } = this.props;
    const asLink = linkAs === 'header' && link;

    const { currentContent } = this.state;

    return (
      <Card fluid>
        {!cardInlinePhoto && image && <Image src={image} fluid />}
        <Card.Content>
          {cardInlinePhoto && image && (
            <Image
              className="itemelement-card-image"
              floated={cardInlinePhoto}
              size={imageSize}
              circular={imageCircular}
              src={image}
            />
          )}
          {header && (
            <Card.Header
              className="itemelement-card-header"
              as={asLink ? 'a' : ''}
              href={asLink ? link : ''}
              textAlign={itemCenterContent ? 'center' : 'left'}
            >
              {header}
              {headerIcon}
            </Card.Header>
          )}
          {subheader && (
            <Card.Meta
              className="itemelement-card-subheader"
              textAlign={itemCenterContent ? 'center' : 'left'}
            >
              {subheader}
            </Card.Meta>
          )}
          <div>
            <Card.Description textAlign={itemCenterContent ? 'center' : 'left'}>
              {currentContent}
            </Card.Description>
          </div>
        </Card.Content>
        {this.contentExceedsCharLimit() && (
          <Card.Content textAlign={itemCenterContent ? 'center' : 'left'} extra>
            {this.renderEventButton(true)}
          </Card.Content>
        )}
      </Card>
    );
  }

  renderVerticalItem() {
    const {
      content,
      imageSize,
      imageCircular,
      verticalItemAlign,
      link,
      linkAs,
      header,
      headerIcon,
      image,
      subheader,
    } = this.props;

    const { currentContent } = this.state;

    return (
      <div>
        {image && (
          <Image
            src={image}
            size={imageSize}
            circular={imageCircular}
            style={{
              margin: verticalItemAlign === 'center' ? '0 auto -1em auto' : '0',
            }}
          />
        )}
        {header && (
          <Header icon textAlign={verticalItemAlign}>
            <Header.Content
              className={`itemelement-vertical-header itemelement-vertical-header-${verticalItemAlign}`}
              as={linkAs === 'header' ? 'a' : 'h2'}
              href={linkAs === 'header' ? link : ''}
            >
              {header}
              {headerIcon}
            </Header.Content>
            {subheader && (
              <Header.Subheader className="itemelement-vertical-subheader">
                {subheader}
              </Header.Subheader>
            )}
          </Header>
        )}
        <div style={{ textAlign: verticalItemAlign }} className="itemelement-vertical-content">
          {content && currentContent}
          {this.contentExceedsCharLimit() && <div>{this.renderEventButton(false)}</div>}
        </div>
      </div>
    );
  }

  renderHorizontalItem() {
    const {
      content,
      imageSize,
      link,
      linkAs,
      header,
      headerIcon,
      image,
      subheader,
      itemCenterContent,
      itemButtonFloat,
    } = this.props;

    const { currentContent } = this.state;

    return (
      <Item>
        {image && <Item.Image size={imageSize} src={image} />}
        <Item.Content verticalAlign={itemCenterContent ? 'middle' : 'top'}>
          {header && (
            <Item.Header
              className="itemelement-horizontal-header"
              as={linkAs === 'header' && link ? 'a' : ''}
              href={linkAs === 'header' ? link : ''}
            >
              {header}
              {headerIcon}
            </Item.Header>
          )}
          {subheader && <Item.Meta>{subheader}</Item.Meta>}
          {content && <Item.Description>{currentContent}</Item.Description>}
          {this.contentExceedsCharLimit() && (
            <Item.Extra>{this.renderEventButton(false, itemButtonFloat)}</Item.Extra>
          )}
        </Item.Content>
      </Item>
    );
  }

  render() {
    const { as } = this.props;

    if (as === 'verticalItem') return this.renderVerticalItem();
    if (as === 'horizontalItem') return this.renderHorizontalItem();
    if (as === 'card') return this.renderCard();

    return <div />;
  }
}

ItemElement.propTypes = {
  header: PropTypes.string.isRequired,
  as: PropTypes.oneOf(['verticalItem', 'horizontalItem', 'card']),
  buttonBasic: PropTypes.bool,
  buttonColor: PropTypes.string,
  buttonSize: PropTypes.string,
  cardInlinePhoto: PropTypes.oneOf(['left', false]),
  charLimit: PropTypes.number,
  content: PropTypes.string,
  headerIcon: PropTypes.element,
  image: PropTypes.string,
  imageCircular: PropTypes.bool,
  imageSize: PropTypes.string,
  itemButtonFloat: PropTypes.oneOf(['left', 'right']),
  itemCenterContent: PropTypes.bool,
  link: PropTypes.string,
  linkAs: PropTypes.oneOf(['header', 'none']),
  subheader: PropTypes.string,
  verticalItemAlign: PropTypes.oneOf(['left', 'right', 'center']),
  event: PropTypes.shape({
    category: PropTypes.string,
    action: PropTypes.string,
    label: PropTypes.string,
  }),
};

ItemElement.defaultProps = {
  as: 'horizontalItem',
  buttonColor: 'blue',
  buttonBasic: false,
  buttonSize: 'medium',
  cardInlinePhoto: false,
  charLimit: 150,
  content: '',
  headerIcon: null,
  image: '',
  imageCircular: false,
  imageSize: 'small',
  itemButtonFloat: 'left',
  itemCenterContent: false,
  link: '',
  linkAs: 'none',
  subheader: '',
  verticalItemAlign: 'center',
  event: {},
};

ItemElement.propOptions = {
  buttonColor: ['primary', 'secondary'],
};
